import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, ChevronDownIcon, MousePointerClickIcon } from 'lucide-react';
import { SlideFrame } from '../SlideFrame';
import { BrowserFrame } from '../BrowserFrame';
import { Cursor } from '../Cursor';
import { FRICTION_POINTS } from '../../../data/deck';
import { useSequence } from '../../../hooks/useSequence';
const EASE = [0.23, 1, 0.32, 1] as const;
interface MiniSiteProps {
  url: string;
  brand: string;
  filters: string[];
  caption: string;
  clicksToResult: string;
  path: {
    x: number;
    y: number;
  }[];
  stepMs: number;
  startDelay: number;
  active: boolean;
  revealDelay: number;
}

/**
 * A commercially familiar filter-rail layout — the pattern every category
 * shares. The cursor works its way down the rail so the effort is visible.
 */
function MiniSite({
  url,
  brand,
  filters,
  caption,
  clicksToResult,
  path,
  stepMs,
  startDelay,
  active,
  revealDelay
}: MiniSiteProps) {
  const step = useSequence(path.map((_, index) => index === 0 ? startDelay : stepMs), active);
  const position = path[Math.min(step, path.length - 1)];
  const appliedFilters = Math.min(step, filters.length);
  return <motion.div className="flex-1 min-w-0 flex flex-col" initial={{
    opacity: 0,
    y: 18
  }} animate={active ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 18
  }} transition={{
    duration: 0.5,
    ease: EASE,
    delay: revealDelay
  }} data-id="element-1945" data-mp="3nlj5zf56xwh">
      <div className="relative" data-id="element-1946" data-mp="wdbwi68y9vrj">
        <BrowserFrame url={url} dense className="h-[272px]" data-id="element-1947" data-mp="wa6a4yrph7zi">
          <div className="flex flex-col h-full" data-id="element-1948" data-mp="10cb3dwe8s2lm">
            <div className="flex items-center gap-2 h-8 px-2.5 shrink-0 border-b border-slate-100" data-id="element-1949" data-mp="mdth833ziy5t">
              <span className="text-[8px] font-bold uppercase tracking-[0.18em] text-dark-navy" data-id="element-1950" data-mp="rcxsczagz5ys">
                {brand}
              </span>
              <div className="flex items-center gap-1.5 ml-1" data-id="element-1951" data-mp="373aiem0qram9">
                {['Shop', 'Deals', 'Help'].map(item => <span key={item} className="text-[7px] text-slate-400" data-id="element-1952" data-mp="3c4g8sd6xilsm">
                    {item}
                  </span>)}
              </div>
              <div className="flex items-center gap-1 ml-auto h-4 px-1.5 rounded bg-slate-50 border border-slate-100" data-id="element-1953" data-mp="2o0i2l00i9jdc">
                <SearchIcon className="w-2 h-2 text-slate-300" data-id="element-1954" data-mp="2j75qtqgxd720" />
                <span className="text-[6.5px] text-slate-300" data-id="element-1955" data-mp="2ea5ita0ilu89">Search</span>
              </div>
            </div>

            <div className="flex flex-1 min-h-0" data-id="element-1956" data-mp="29blx9apu2kgf">
              <div className="w-[86px] shrink-0 border-r border-slate-100 p-2 space-y-1.5" data-id="element-1957" data-mp="2986i67w5tbf0">
                <p className="text-[6.5px] font-semibold uppercase tracking-[0.14em] text-slate-400" data-id="element-1958" data-mp="24dkcmlcred2u">
                  Filters
                </p>
                {filters.map((filter, index) => {
                const on = index < appliedFilters;
                return <motion.div key={filter} className="flex items-center justify-between px-1.5 py-1 rounded border" animate={{
                  backgroundColor: on ? 'rgba(67,56,202,0.05)' : 'rgba(255,255,255,1)',
                  borderColor: on ? 'rgba(67,56,202,0.22)' : 'rgba(241,245,249,1)'
                }} transition={{
                  duration: 0.25,
                  ease: EASE
                }} data-id="element-1959" data-mp="3un46weaistcq" data-mpi={index}>
                      <span className={`text-[7px] ${on ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-1960" data-mp="fral4xg50uiv">
                        {filter}
                      </span>
                      <ChevronDownIcon className="w-1.5 h-1.5 text-slate-300" data-id="element-1961" data-mp="3j8sq8x4iznfl" />
                    </motion.div>;
              })}
                <div className="pt-1 space-y-1" data-id="element-1962" data-mp="3s9qmuucmwso5">
                  <div className="h-1 w-full rounded-full bg-slate-100" data-id="element-1963" data-mp="3sajwc32z9w9z" />
                  <div className="h-1 w-2/3 rounded-full bg-slate-100" data-id="element-1964" data-mp="13gmp9iwlyceb" />
                </div>
              </div>

              <div className="flex-1 min-w-0 p-2" data-id="element-1965" data-mp="17hf0ghp1ird8">
                <div className="flex items-center justify-between mb-1.5" data-id="element-1966" data-mp="161ayfcpv3rex">
                  <div className="h-1.5 w-14 rounded-full bg-slate-200" data-id="element-1967" data-mp="163no6c1v362j" />
                  <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-100" data-id="element-1968" data-mp="114kkauxmb5w5">
                    <span className="text-[6.5px] text-slate-400" data-id="element-1969" data-mp="1fxxhbvymo8eb">Sort</span>
                    <ChevronDownIcon className="w-1.5 h-1.5 text-slate-300" data-id="element-1970" data-mp="1azcgpvfgrmpc" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2" data-id="element-1971" data-mp="1oylxaw7l0itu">
                  {Array.from({
                  length: 4
                }).map((_, index) => <div key={index} className="rounded-lg border border-slate-100 overflow-hidden" data-id="element-1972" data-mp="1yqi4qxvk5laq" data-mpi={index}>
                      <div className="h-[52px] bg-slate-100" data-id="element-1973" data-mp="1ts6vopd37etk" />
                      <div className="p-1.5 space-y-1" data-id="element-1974" data-mp="27s7yiacwwwbo">
                        <div className="h-1 w-3/4 rounded-full bg-slate-200" data-id="element-1975" data-mp="27otyv9vmv17x" />
                        <div className="h-1 w-1/2 rounded-full bg-slate-100" data-id="element-1976" data-mp="23rfld60x67a4" />
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </BrowserFrame>

        <Cursor x={position.x} y={position.y} clickId={step} visible={step > 0} data-id="element-1977" data-mp="2ddqd7qxhfcj0" />
      </div>

      <div className="flex items-center justify-between mt-3" data-id="element-1978" data-mp="2ibs6t5sjau8q">
        <span className="text-[11px] font-medium text-dark-navy" data-id="element-1979" data-mp="2ieed723ov3pd">{caption}</span>
        <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-error/[0.06] border border-error/20" data-id="element-1980" data-mp="23luag2kgay8g">
          <MousePointerClickIcon className="w-2.5 h-2.5 text-error" data-id="element-1981" data-mp="28g6y6osymvq0" />
          <span className="text-[9.5px] font-semibold text-error" data-id="element-1982" data-mp="1uo0hn1lwamq1">{clicksToResult}</span>
        </span>
      </div>
    </motion.div>;
}
const SITES: Omit<MiniSiteProps, 'active'>[] = [{
  url: 'driveline-rentals.ae',
  brand: 'Driveline',
  filters: ['Location', 'Dates', 'Budget', 'Vehicle type'],
  caption: 'Car rental',
  clicksToResult: '14 clicks',
  path: [{
    x: 40,
    y: 92
  }, {
    x: 40,
    y: 116
  }, {
    x: 40,
    y: 140
  }, {
    x: 40,
    y: 164
  }, {
    x: 300,
    y: 74
  }, {
    x: 190,
    y: 170
  }],
  stepMs: 780,
  startDelay: 900,
  revealDelay: 0.25
}, {
  url: 'atelier-store.com',
  brand: 'Atelier',
  filters: ['Category', 'Size', 'Colour', 'Price'],
  caption: 'Fashion retail',
  clicksToResult: '11 clicks',
  path: [{
    x: 40,
    y: 92
  }, {
    x: 40,
    y: 116
  }, {
    x: 40,
    y: 140
  }, {
    x: 300,
    y: 74
  }, {
    x: 190,
    y: 170
  }, {
    x: 40,
    y: 164
  }],
  stepMs: 860,
  startDelay: 1250,
  revealDelay: 0.35
}, {
  url: 'marketvault.com',
  brand: 'MarketVault',
  filters: ['Department', 'Brand', 'Rating', 'Delivery'],
  caption: 'E-commerce store',
  clicksToResult: '17 clicks',
  path: [{
    x: 40,
    y: 92
  }, {
    x: 300,
    y: 74
  }, {
    x: 40,
    y: 116
  }, {
    x: 40,
    y: 140
  }, {
    x: 40,
    y: 164
  }, {
    x: 190,
    y: 170
  }],
  stepMs: 720,
  startDelay: 1550,
  revealDelay: 0.45
}];
export function SlideFriction({
  active
}: {
  active: boolean;
}) {
  return <SlideFrame eyebrow="The problem" title="Traditional websites make customers do the work" lead="Whatever the category, the pattern is identical: the customer is handed the search, the filters, and the comparison — and asked to figure it out." data-id="element-1983" data-mp="2fse1j5fen3ul">
      <div className="h-full flex gap-8" data-id="element-1984" data-mp="2ti915csd0dvn">
        <div className="w-[212px] shrink-0" data-id="element-1985" data-mp="2tldbkrwf45ur">
          <p className="text-[9.5px] font-semibold uppercase tracking-[0.2em] text-slate-400" data-id="element-1986" data-mp="2yhklswvuk3ii">
            Today the customer must
          </p>
          <ul className="mt-4 space-y-2.5" data-id="element-1987" data-mp="33i8d7jnzmmd9">
            {FRICTION_POINTS.map((point, index) => <motion.li key={point} className="flex items-start gap-2.5" initial={{
            opacity: 0,
            x: -10
          }} animate={active ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: -10
          }} transition={{
            duration: 0.4,
            ease: EASE,
            delay: 0.25 + index * 0.08
          }} data-id="element-1988" data-mp="38frwwz3gfrcl" data-mpi={index}>
                <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-error shrink-0" data-id="element-1989" data-mp="3b1hjv55406ym" />
                <span className="text-[12.5px] leading-snug text-slate-600" data-id="element-1990" data-mp="3ppvsxxt4crnm">{point}</span>
              </motion.li>)}
          </ul>
        </div>

        <div className="flex-1 min-w-0 flex gap-5" data-id="element-1991" data-mp="3krauo3xfgi1t">
          {SITES.map(site => <MiniSite key={site.url} {...site} active={active} data-id="element-1992" data-mp="2x1zqsdks67jg" />)}
        </div>
      </div>
    </SlideFrame>;
}